import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  CloudDownloadIcon,
  DownloadFileIcon,
  EditDocumentIcon,
  EyeIconSmall,
  SendReminderIcon,
} from '../../../../../../assets/icons/index';
import { Actions } from '../../../../../../components/actions/index';
import Button from '../../../../../../components/button/index';
import { TableRow } from '../../../../../../components/table/index';
import { Tag, TagVariant } from '../../../../../../components/tag/index';
import { Tooltip } from '../../../../../../components/tooltip/index';
import { useFormat, useLocale } from '../../../../../../hooks/index';
import { selectedCompanyStorage } from '../../../../../../storage/company/selected-company.util';
import { getSignatureCategoryDisplayLabel } from '../../../../constants/document-category.constant';
import { SignaturePopupType } from '../../../enums/signature-popup-type.const';
import {
  SignatureStatus,
  signatureStatusTranslationKey,
} from '../../../enums/signature-status.const';
import { useCanSendSignatureReminder } from '../../../hooks/use-can-send-signature-reminder.hook';
import { useDownloadSignatureDocument } from '../../../hooks/use-download-signature-document.hook';
import { SignaturePopupStore } from '../../../store/signature-popup.store';
import { SignatoryRow } from '../../../types/signature-row.types';
import { toViewDocumentDetailsMetadata } from '../../../utils/to-view-document-details-metadata.util';
import { Column, columnOrderMap } from '../signature-table.types';
import { SignatureMobileCard } from './signature-mobile-row';
import { RowPayload, RowProps } from './signature-row.types';

const Signatories: FC<{ signatories: SignatoryRow[]; totalCount: number }> = ({
  signatories,
  totalCount,
}) => {
  return (
    <div className="flex items-center gap-2">
      {signatories?.slice(0, 5).map((s) => {
        const fullName = s.stakeholder?.fullName ?? '-';
        return (
          <Tooltip childrenWrapperClassName="overflow-visible" content={fullName} key={s.id}>
            <div className="relative inline-flex">
              <span
                className={twMerge(
                  'flex size-10 items-center justify-center rounded-full text-m font-medium',
                  s.isSigned ? 'bg-forest-50 text-forest-600' : 'bg-gray-100 text-gray-500',
                  s.isSigned ? 'text-forest-600' : 'text-gray-500',
                )}
              >
                {(fullName?.[0] ?? '-').toUpperCase()}
              </span>
              <div
                className={twMerge(
                  'absolute bottom-0 right-0 size-[13px] rounded-full border-[1.5px] border-white',
                  s.isSigned ? 'bg-forest-500' : 'bg-gray-300',
                )}
              />
            </div>
          </Tooltip>
        );
      })}
      {totalCount > 5 && <span className="text-sm text-gray-500">+{totalCount - 5}</span>}
    </div>
  );
};

const getStatusVariant = (status: SignatureStatus): TagVariant => {
  switch (status) {
    case SignatureStatus.DOCUMENT_EXPIRED:
      return TagVariant.RED;
    case SignatureStatus.FULLY_SIGNED:
      return TagVariant.GREEN;
    case SignatureStatus.PARTIALLY_SIGNED:
      return TagVariant.BLUE;
    case SignatureStatus.SIGNATURE_PENDING:
      return TagVariant.YELLOW;
    case SignatureStatus.VOIDED:
      return TagVariant.GRAY;
    default:
      return TagVariant.GRAY;
  }
};

export const Row: FC<RowProps> = ({ columns, data, isMySignaturesScope, mode, trClassName }) => {
  const { format } = useFormat();
  const { messagesLocale } = useLocale();
  const { download } = useDownloadSignatureDocument();
  const openPopup = SignaturePopupStore(({ openPopup }) => openPopup);
  const { name, category, status, signatories, lastSentAt, expiresAt, id } = data;

  const categoryLabel = getSignatureCategoryDisplayLabel(category, messagesLocale);

  const totalCount = signatories?.length ?? 0;
  const signedCount = signatories?.filter((s) => s.isSigned).length ?? 0;

  const lastSentLabel = lastSentAt ? format(new Date(lastSentAt), 'dd MMM, yyyy') : '-';
  const expiryLabel = expiresAt ? format(new Date(expiresAt), 'dd MMM yyyy') : '-';

  const currentUserStakeholderId = selectedCompanyStorage.getState().company?.stakeholder?.id;
  const currentUserSignatory = currentUserStakeholderId
    ? signatories?.find((s) => s.stakeholderId === currentUserStakeholderId)
    : signatories?.[0];
  const isCurrentUserSigned = currentUserSignatory?.isSigned ?? false;
  const isExpired = status === SignatureStatus.DOCUMENT_EXPIRED;
  const isVoided = status === SignatureStatus.VOIDED;
  const usePersonalizedMySignaturesStatus = isMySignaturesScope && !isVoided;

  const getMySignaturesStatus = (): { label: string; variant: TagVariant } => {
    if (isExpired) {
      return { label: messagesLocale['EXPIRED'], variant: TagVariant.RED };
    }
    if (isCurrentUserSigned) {
      return { label: messagesLocale['SIGNED'], variant: TagVariant.GREEN };
    }
    return {
      label: messagesLocale['SIGNATURE_STATUS_SIGNATURE_PENDING'],
      variant: TagVariant.YELLOW,
    };
  };

  const baseStatusLabel =
    status in signatureStatusTranslationKey
      ? messagesLocale[signatureStatusTranslationKey[status as SignatureStatus]]
      : status;
  const mySignaturesStatus = usePersonalizedMySignaturesStatus ? getMySignaturesStatus() : null;
  const statusLabel = mySignaturesStatus
    ? mySignaturesStatus.label
    : status === SignatureStatus.PARTIALLY_SIGNED && totalCount > 0
      ? `${baseStatusLabel} ${signedCount}/${totalCount}`
      : baseStatusLabel;

  const statusVariant = mySignaturesStatus ? mySignaturesStatus.variant : getStatusVariant(status);

  const canSendReminder = useCanSendSignatureReminder(status);
  const showSendReminder = !isMySignaturesScope && canSendReminder;

  const sendReminderAction = {
    title: messagesLocale['SEND_REMINDER'],
    icon: <SendReminderIcon className="size-4" iconColor="#344054" />,
    callback: () =>
      openPopup({
        metadata: { documentName: name, signatureId: id },
        type: SignaturePopupType.SEND_REMINDER,
      }),
  };

  const viewDetailsAction = {
    title: messagesLocale['VIEW_DETAILS'],
    icon: <EyeIconSmall />,
    callback: () =>
      openPopup({
        metadata: toViewDocumentDetailsMetadata(data),
        type: SignaturePopupType.VIEW_DOCUMENT_DETAILS,
      }),
  };

  const payload: RowPayload = {
    [Column.DOCUMENT_NAME]: <span className="">{name}</span>,
    [Column.CATEGORY]: <span className="">{categoryLabel}</span>,
    [Column.STATUS]: <Tag title={statusLabel} variant={statusVariant} />,
    [Column.SIGNATORIES]: <Signatories signatories={signatories} totalCount={totalCount} />,
    [Column.LAST_SENT]: <span className="text-sm font-[450] text-gray-700">{lastSentLabel}</span>,
    [Column.EXPIRY]: <span className="text-sm font-[450] text-gray-700">{expiryLabel}</span>,
    [Column.ACTIONS]: isMySignaturesScope ? (
      <div className="flex items-center justify-end gap-2 pr-6">
        {!isCurrentUserSigned && (
          <Button
            className="gap-1.5 px-3 py-1.5 font-normal"
            disabled={isExpired || isVoided}
            onClick={() =>
              openPopup({
                metadata: { documentName: name, signatureId: id },
                type: SignaturePopupType.SIGN_DOCUMENT,
              })
            }
            type="button"
          >
            <EditDocumentIcon
              className="size-[18px]"
              iconColor={isExpired || isVoided ? '#d1d6de' : '#fff'}
            />
            {messagesLocale['SIGN_DOCUMENT']}
          </Button>
        )}
        <Actions
          actions={[
            viewDetailsAction,
            {
              title: messagesLocale['REVIEW'],
              icon: <DownloadFileIcon iconColor="#344054" />,
              disabled: isExpired,
              callback: () =>
                openPopup({
                  metadata: { documentName: name, signatureId: id },
                  type: SignaturePopupType.VIEW_DOCUMENT,
                }),
            },
            {
              title: messagesLocale['DOWNLOAD'],
              icon: <CloudDownloadIcon iconColor="#344054" />,
              callback: () => download(id),
            },
          ]}
        />
      </div>
    ) : (
      <Actions
        actions={[
          viewDetailsAction,
          ...(showSendReminder ? [sendReminderAction] : []),
          {
            title: messagesLocale['VIEW_DOCUMENT'],
            icon: <DownloadFileIcon iconColor="#344054" />,
            callback: () =>
              openPopup({
                metadata: { documentName: name, signatureId: id },
                type: SignaturePopupType.VIEW_DOCUMENT,
              }),
          },
          {
            title: messagesLocale['DOWNLOAD'],
            icon: <CloudDownloadIcon iconColor="#344054" />,
            callback: () => download(id),
          },
        ]}
      />
    ),
    id,
  };

  const columnOrder = columns ?? columnOrderMap;
  const isStickyOnly = columns?.length === 1 && columns[0] === Column.DOCUMENT_NAME;
  const rowBgClass = trClassName ?? 'bg-white';

  if (mode === 'table')
    return (
      <TableRow
        columnOrder={columnOrder}
        contentClassName={{
          [Column.ACTIONS]: 'justify-end pr-6',
          general: 'pl-4 pr-2',
        }}
        fields={payload}
        labelClassName={{
          [Column.ACTIONS]: 'text-sm font-[450] text-gray-700',
          general: 'text-sm font-[450] text-gray-700',
        }}
        tdClassName={
          isStickyOnly
            ? { [Column.DOCUMENT_NAME]: `min-w-[400px] ${rowBgClass}`, general: rowBgClass }
            : { [Column.ACTIONS]: `w-24 min-w-[96px] ${rowBgClass}`, general: rowBgClass }
        }
        trClassName={trClassName}
      />
    );

  return <SignatureMobileCard data={data} isMySignaturesScope={isMySignaturesScope} />;
};
